import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { User, Camera } from "react-feather";
import { agencyService } from "../../../services/agency.service";
import { Button } from "../../../components/ui/button";


function PersonalDetailsSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      {/* Profile Picture Skeleton */}
      <div className="flex items-center space-x-4">
        <div className="w-24 h-24 bg-muted rounded-full" />
        <div className="space-y-2">
          <div className="h-4 w-32 bg-muted rounded" />
          <div className="h-4 w-24 bg-muted rounded" />
        </div>
      </div>

      {/* Form Fields Skeleton */}
      <div className="grid grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i}>
            <div className="h-5 w-20 bg-muted mb-2 rounded" />
            <div className="h-10 bg-muted rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function PersonalDetails() {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  
  // Fetch agency profile
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ["agency-profile"],
    queryFn: () => agencyService.getProfile(),
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    profilePicture: "",
  });

  // Update form when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        email: profile.email || "",
        profilePicture: profile.profilePicture || "",
      });
    }
  }, [profile]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: agencyService.updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-profile"] });
      toast.success("Profile updated successfully!");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update profile");
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleProfilePictureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create a temporary URL for preview
      const url = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        profilePicture: url,
      }));
      
      // TODO: In a real app, you would upload the file to a storage service
      // and get back a permanent URL to save in the database
      toast("File selected. In production, this would be uploaded to cloud storage.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Only send changed fields
      const changedFields: any = {};
      if (formData.firstName !== profile?.firstName) {
        changedFields.firstName = formData.firstName;
      }
      if (formData.lastName !== profile?.lastName) {
        changedFields.lastName = formData.lastName;
      }
      if (formData.email !== profile?.email) {
        changedFields.email = formData.email;
      }
      if (formData.profilePicture !== profile?.profilePicture) {
        changedFields.profilePicture = formData.profilePicture;
      }

      if (Object.keys(changedFields).length === 0) {
        toast("No changes to save");
        return;
      }

      await updateProfileMutation.mutateAsync(changedFields);
    } catch (error) {
      // Error handling is done in the mutation
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <h2 className="text-lg font-semibold mb-1">Personal Details</h2>
        <p className="text-muted-foreground mb-6">
          Update your personal information and profile picture.
        </p>
        <PersonalDetailsSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h2 className="text-lg font-semibold mb-1">Personal Details</h2>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Failed to load profile information. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-semibold mb-1">Personal Details</h2>
      <p className="text-muted-foreground mb-6">
        Update your personal information and profile picture.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Picture Section */}
        <div className="flex items-center space-x-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden">
              {formData.profilePicture ? (
                <img
                  src={formData.profilePicture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-foreground">Profile Picture</h3>
            <p className="text-sm text-muted-foreground">
              Upload a new profile picture or remove the current one.
            </p>
            <div className="space-y-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                className="hidden"
                id="profile-picture-upload"
              />
              <label
                htmlFor="profile-picture-upload"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100 cursor-pointer"
              >
                <Camera className="w-4 h-4" />
                Change Picture
              </label>
              {formData.profilePicture && (
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, profilePicture: "" }))}
                  className="block text-sm text-red-600 hover:text-red-800"
                >
                  Remove Picture
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-muted-foreground mb-2"
            >
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-muted-foreground mb-2"
            >
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              required
            />
          </div>

          {/* Email */}
          <div className="md:col-span-2">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-muted-foreground mb-2"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              required
            />
          </div>

          {/* Agency Code (Read-only) */}
          {profile?.code && (
            <div className="md:col-span-2">
              <label
                htmlFor="code"
                className="block text-sm font-medium text-muted-foreground mb-2"
              >
                Agency Code
              </label>
              <input
                type="text"
                id="code"
                value={profile.code}
                readOnly
                disabled
                className="w-full px-3 py-2 border border-border rounded-lg cursor-not-allowed text-muted-foreground"
              />
              <p className="text-sm text-muted-foreground mt-1">
                This is your unique agency referral code. Brands will use this code to connect with your agency.
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            disabled={isSaving || updateProfileMutation.isPending}
            onClick={() => {
              if (profile) {
                setFormData({
                  firstName: profile.firstName || "",
                  lastName: profile.lastName || "",
                  email: profile.email || "",
                  profilePicture: profile.profilePicture || "",
                });
              }
            }}
            className="px-4 py-2 border border-border rounded-lg hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <Button
            type="submit"
            disabled={isSaving || updateProfileMutation.isPending}
          >
            {isSaving || updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}